import { useState, useEffect } from 'react';
import { FormikProps, FormikValues, useFormikContext } from 'formik';
import { find } from 'lodash';
import { Discount, FormModel } from '../../../../models/form/form';
import { PaymentOption } from '../../../../models/form/paymentOption';
import { FieldType } from '../../../../models/form/fieldType';
import { FieldModel } from '../../../../models/form/field';
import { v4 as uuidv4 } from 'uuid';
import { IntlShape, useIntl } from 'react-intl';
import { getText } from '../../../../utilities/CultureUtilities';

interface SummaryField {
  fieldName: string;
  options: PaymentOption[];
}

export const calculateSummary = (
  values: FormikValues,
  form: FormModel,
  intl: IntlShape,
  discount: Discount,
): { total: number; summary: SummaryField[] } => {
  const fields: { [id: string]: FieldModel } = {};
  for (const step of form.content.steps) {
    for (const section of step.sections) {
      for (const field of section.fields) {
        fields[field.id] = field;
      }
    }
  }

  return getSummary(values, fields, intl, form.fixedAmount ?? undefined, discount);
};

const getSummary = (values, fields, intl, fixedAmount?, discount?): { total: number; summary: SummaryField[] } => {
  let total = 0;
  const summary: SummaryField[] = [];
  if (fixedAmount) {
    const fixedAmountOption: PaymentOption[] = [];
    fixedAmountOption.push({
      price: fixedAmount ?? 0,
      description: intl.formatMessage({ id: 'formPublic.formAmountSummaryText' }),
      value: uuidv4(),
      text: intl.formatMessage({ id: 'formPublic.formAmountSummaryText' }),
    });
    total = total + fixedAmount;
    summary.push({ fieldName: '', options: fixedAmountOption });
  }

  for (const value in values) {
    if (fields[value]) {
      const fieldDefinition = fields[value];
      switch (fieldDefinition.type) {
        case FieldType.PaymentChoiceList:
        case FieldType.PaymentOptionList:
          const selectedValues = values[value];

          if (selectedValues.length > 0) {
            let options: PaymentOption[] = [];
            switch (fieldDefinition.type) {
              case FieldType.PaymentChoiceList:
                options = fieldDefinition.choices;
                break;
              case FieldType.PaymentOptionList:
                options = fieldDefinition.options;
                break;
            }

            const optionsDefinition: PaymentOption[] = [];
            for (const value of selectedValues) {
              const selectedOption = find(options, { value });
              if (selectedOption) {
                total = total + selectedOption.price;
                optionsDefinition.push({ ...selectedOption, text: selectedOption.text });
              }
            }

            if (options.length > 0) {
              summary.push({ fieldName: fieldDefinition.text, options: optionsDefinition });
            }
          }
          break;
        case FieldType.Products:
          const selectedProducts = values[value];
          const optionsDefinition: PaymentOption[] = [];
          for (const product of selectedProducts) {
            const quantity = product.quantity;
            if (quantity && quantity > 0) {
              const productDefinition = find(fieldDefinition.products, { value: product.id });
              let price = productDefinition.price * quantity;
              if (price < 0) {
                price = 0;
              }

              total = total + price;
              optionsDefinition.push({
                ...productDefinition,
                price: price,
                text: `${getText(productDefinition.text, intl.locale)}  x${quantity}`,
              });
            }
          }

          if (optionsDefinition.length > 0) {
            summary.push({ fieldName: fieldDefinition.text, options: optionsDefinition });
          }
          break;
      }
    }
  }

  if (discount) {
    const { type, value, code } = discount;
    let totalDiscount = 0;
    if (type == 'percent') {
      totalDiscount = (value / 100) * total;
      total = (1 - value / 100) * total;
    } else if (type == 'amount') {
      totalDiscount = value * 100;
      total = total - value * 100;
    }

    total = total > 0 ? total : 0;

    summary.push({
      fieldName: intl.formatMessage({ id: 'formPublic.discount' }),
      options: [
        {
          text: intl.formatMessage({ id: 'formPublic.discountCodeSummary' }, { code }),
          price: totalDiscount * -1,
          description: intl.formatMessage({ id: 'formPublic.discountCodeSummary' }, { code }),
          value: uuidv4(),
        },
      ],
    });
  }

  return { total, summary };
};

const useFormSummary = (
  form: FormModel,
  discount: Discount,
): { total: number; summary: { fieldName: string; options: PaymentOption[] }[] } => {
  const [total, setTotal] = useState(0);
  const [fields, setFields] = useState<{ [id: string]: FieldModel }>({});
  const [summary, setSummary] = useState<{ fieldName: string; options: PaymentOption[] }[]>([]);
  const formikContext = useFormikContext<FormikProps<FormikValues>>();
  const intl = useIntl();

  useEffect(() => {
    const _fields: { [id: string]: FieldModel } = {};
    for (const step of form.content.steps) {
      for (const section of step.sections) {
        for (const field of section.fields) {
          _fields[field.id] = field;
        }
      }
    }

    setFields(_fields);
  }, [form]);

  useEffect(() => {
    const { total: _total, summary: _summary } = getSummary(
      formikContext.values,
      fields,
      intl,
      form.fixedAmount ?? undefined,
      discount,
    );
    setSummary(_summary);
    setTotal(_total);
  }, [formikContext.values, discount]);

  return { total, summary };
};

export default useFormSummary;
